import dayjs from "dayjs";
import { produce } from "immer";
import { create } from "zustand";

export const CRM_PET_DIAGNOSIS_TABS = {
  overview: "overview",
  activity: "activity",
  results: "results",
};

function createPeriodFilter(extra = {}) {
  return {
    period: "30",
    from: dayjs().subtract(29, "day").format("YYYY-MM-DD"),
    to: dayjs().format("YYYY-MM-DD"),
    ...extra,
  };
}

export function createCrmPetDiagnosisFilters() {
  return {
    [CRM_PET_DIAGNOSIS_TABS.overview]: createPeriodFilter({ minDays: "30" }),
    [CRM_PET_DIAGNOSIS_TABS.activity]: createPeriodFilter(),
    [CRM_PET_DIAGNOSIS_TABS.results]: createPeriodFilter({
      minDays: "30",
      status: "all",
      contacted: "true",
    }),
  };
}

function createSectionState() {
  return {
    usage: false,
    overviewKpis: false,
    overviewContactedKpis: false,
    resultsKpis: false,
    resultsContactedKpis: false,
    attempts: false,
  };
}

function createErrorsState() {
  return {
    usage: null,
    overviewKpis: null,
    overviewContactedKpis: null,
    resultsKpis: null,
    resultsContactedKpis: null,
    attempts: null,
  };
}

function createPagination(perPage) {
  return {
    page: 0,
    perPage,
    totalPages: 1,
    totalDocuments: 0,
  };
}

function cloneFilters(filters) {
  return JSON.parse(JSON.stringify(filters));
}

function createDefaultData() {
  const filters = createCrmPetDiagnosisFilters();

  return {
    filters,
    appliedFilters: cloneFilters(filters),
    loading: createSectionState(),
    errors: createErrorsState(),
    usage: {
      summary: null,
      daily: [],
    },
    overviewKpis: null,
    overviewContactedKpis: null,
    resultsKpis: null,
    resultsContactedKpis: null,
    attempts: [],
    attemptsPagination: createPagination(20),
    lastUpdatedAt: null,
  };
}

export const useCrmPetDiagnosis = create((set) => ({
  data: createDefaultData(),
  actions: {
    setFilters: (tab, filters) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.filters[tab] = { ...state.data.filters[tab], ...filters };
        }),
      ),
    setAppliedFilters: (tab, filters) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.appliedFilters[tab] = { ...filters };
        }),
      ),
    setAllFilters: (filters) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.filters = cloneFilters(filters);
          draft.data.appliedFilters = cloneFilters(filters);
        }),
      ),
    setLoading: (section, loading) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.loading[section] = loading;
        }),
      ),
    setError: (section, error) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.errors[section] = error;
        }),
      ),
    setUsage: (usage) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.usage = usage;
          draft.data.loading.usage = false;
          draft.data.errors.usage = null;
        }),
      ),
    setKpis: (section, kpis) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data[section] = kpis;
          draft.data.loading[section] = false;
          draft.data.errors[section] = null;
        }),
      ),
    setAttempts: (attempts, pagination) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.attempts = attempts;
          draft.data.attemptsPagination = pagination;
          draft.data.loading.attempts = false;
          draft.data.errors.attempts = null;
        }),
      ),
    setAttemptsPage: (page) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.attemptsPagination.page = page;
        }),
      ),
    setLastUpdatedAt: (lastUpdatedAt) =>
      set((state) =>
        produce(state, (draft) => {
          draft.data.lastUpdatedAt = lastUpdatedAt;
        }),
      ),
    clear: () =>
      set((state) =>
        produce(state, (draft) => {
          draft.data = createDefaultData();
        }),
      ),
  },
}));
